import React, { useState, useEffect, useCallback } from 'react';
import type { SpecialRegulationsVersion, ExtractionStatus, SpecialRegulation } from '../../types';
import { regulationService } from '../../services/regulationService';

/**
 * SpecialRegulationsManager — Admin component for uploading and managing
 * special regulations PDFs and viewing extraction results.
 *
 * Requirement 10.1 — "Special Regulations" navigation link in admin portal sidebar.
 * Requirement 10.2 — Display current version information.
 * Requirement 10.3 — Upload PDF and display extraction status.
 * Requirement 10.4 — Poll status endpoint with progress indicator.
 * Requirement 10.5 — Display extraction results grouped by requirementType.
 * Requirement 10.6 — Empty state when no version exists.
 */

const REQUIREMENT_TYPE_LABELS: Record<string, string> = {
  safety_equipment: 'Safety Equipment',
  crew_qualification: 'Crew Qualification',
  boat_specification: 'Boat Specification',
  navigation: 'Navigation',
  communication: 'Communication',
  medical: 'Medical',
  structural: 'Structural',
  general: 'General',
};

interface SectionGroup {
  requirementType: string;
  sections: SpecialRegulation[];
}

function groupByRequirementType(sections: SpecialRegulation[]): SectionGroup[] {
  const groups: Record<string, SpecialRegulation[]> = {};
  for (const section of sections) {
    if (!groups[section.requirementType]) {
      groups[section.requirementType] = [];
    }
    groups[section.requirementType].push(section);
  }

  return Object.entries(groups)
    .map(([requirementType, secs]) => ({
      requirementType,
      sections: secs.sort((a, b) => a.sectionNumber.localeCompare(b.sectionNumber, undefined, { numeric: true })),
    }))
    .sort((a, b) => a.requirementType.localeCompare(b.requirementType));
}

const SpecialRegulationsManager: React.FC = () => {
  // Versions state (all versions)
  const [versions, setVersions] = useState<SpecialRegulationsVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Extraction status state
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [showExtractionStatus, setShowExtractionStatus] = useState(false);

  // Sections state (for results summary)
  const [sections, setSections] = useState<SpecialRegulation[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);

  // Collapsible group state — all collapsed by default
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const toggleGroup = (requirementType: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(requirementType)) {
        next.delete(requirementType);
      } else {
        next.add(requirementType);
      }
      return next;
    });
  };

  const handleDelete = async (versionId: string) => {
    if (!window.confirm(`Delete Special Regulations version ${versionId}? This will remove all sections and cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await regulationService.deleteVersion(versionId);
      // Refresh versions list
      fetchVersions();
      setSections([]);
    } catch {
      setDeleteError('Failed to delete version.');
    } finally {
      setDeleting(false);
    }
  };

  /** Fetch all versions. */
  const fetchVersions = useCallback(async () => {
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const data = await regulationService.getAllVersions();
      setVersions(data);
    } catch {
      setVersionsError('Failed to load versions.');
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  /** Fetch sections for the current version. */
  const fetchSections = useCallback(async () => {
    setSectionsLoading(true);
    setSectionsError(null);
    try {
      const data = await regulationService.listSections();
      setSections(data);
    } catch {
      setSectionsError('Failed to load regulation sections.');
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  /** Fetch extraction status. */
  const fetchExtractionStatus = useCallback(async () => {
    try {
      const status = await regulationService.getExtractionStatus();
      setExtractionStatus(status);
      return status;
    } catch {
      // Status endpoint may 404 if no extraction exists — that's OK
      setExtractionStatus(null);
      return null;
    }
  }, []);

  // Initial load: fetch versions and extraction status
  useEffect(() => {
    fetchVersions();
    // Check extraction status on load - only show if still processing
    fetchExtractionStatus().then((status) => {
      if (status && status.status === 'processing') {
        setShowExtractionStatus(true);
        setPolling(true);
      }
    });
  }, [fetchVersions, fetchExtractionStatus]);

  // Load sections when versions are available (use latest version)
  useEffect(() => {
    if (versions.length > 0) {
      fetchSections();
    }
  }, [versions, fetchSections]);

  // Polling: poll extraction status every 5 seconds while status is 'processing'
  useEffect(() => {
    if (!polling) return;

    const intervalId = setInterval(async () => {
      const status = await fetchExtractionStatus();
      if (status && status.status !== 'processing') {
        setPolling(false);
        // Refresh versions when extraction completes
        if (status.status === 'complete') {
          fetchVersions();
        }
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [polling, fetchExtractionStatus, fetchVersions]);

  /** Handle PDF upload form submission. */
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !effectiveDate) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const result = await regulationService.uploadSpecialRegulations(uploadFile, effectiveDate);
      setUploadSuccess(`Upload started. Version: ${result.versionId}`);
      setExtractionStatus({ status: 'processing', versionId: result.versionId });
      setShowExtractionStatus(true);
      setPolling(true);
      setUploadFile(null);
      setEffectiveDate('');
      // Reset the file input
      const fileInput = document.getElementById('special-reg-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch {
      setUploadError('Failed to upload special regulations PDF.');
    } finally {
      setUploading(false);
    }
  };

  const sectionGroups = groupByRequirementType(sections);

  return (
    <div className="special-regulations-manager" data-testid="special-regulations-manager">
      <h2>Special Regulations Management</h2>

      {/* Current Version Display */}
      <div data-testid="version-section">
        <h3>All Versions</h3>
        {versionsLoading && <p data-testid="versions-loading">Loading versions…</p>}
        {versionsError && (
          <div className="error-message" data-testid="versions-error">
            {versionsError}
          </div>
        )}
        {!versionsLoading && !versionsError && versions.length === 0 && (
          <div className="info-message" data-testid="empty-state">
            No special regulations have been uploaded yet. Use the form below to upload a
            Special Regulations PDF.
          </div>
        )}
        {!versionsLoading && !versionsError && versions.length > 0 && (
          <table className="global-documents-table" data-testid="versions-table">
            <thead>
              <tr>
                <th>Version ID</th>
                <th>Effective Date</th>
                <th>Section Count</th>
                <th>Uploaded At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {versions.map((ver, index) => (
                <tr key={ver.versionId} data-testid={`version-row-${index}`}>
                  <td data-testid={`version-id-${index}`}>
                    {ver.versionId}
                    {index === 0 && <span style={{ marginLeft: '8px', color: '#28a745', fontWeight: 'bold' }}>✓ Latest</span>}
                  </td>
                  <td data-testid={`version-effective-date-${index}`}>{ver.effectiveDate}</td>
                  <td data-testid={`version-section-count-${index}`}>{ver.sectionCount}</td>
                  <td data-testid={`version-uploaded-at-${index}`}>
                    {new Date(ver.uploadedAt).toLocaleString()}
                  </td>
                  <td>
                    <button
                      onClick={() => handleDelete(ver.versionId)}
                      disabled={deleting}
                      title="Delete this version"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                      data-testid={`delete-version-btn-${index}`}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {deleteError && (
          <div className="error-message" data-testid="delete-error">
            {deleteError}
          </div>
        )}
      </div>

      {/* Extraction Status Indicator - only shown during/after upload in current session */}
      {showExtractionStatus && extractionStatus && extractionStatus.status === 'processing' && (
        <div className="info-message" data-testid="extraction-processing">
          <strong>Extraction in progress…</strong> Version: {extractionStatus.versionId}.
          {extractionStatus.progress && <><br />{extractionStatus.progress}</>}
          {!extractionStatus.progress && ' This may take several minutes.'}
          <br />Status will update automatically.
        </div>
      )}
      {showExtractionStatus && extractionStatus && extractionStatus.status === 'complete' && (
        <div className="success-message" data-testid="extraction-complete">
          Extraction complete. {extractionStatus.sectionCount} sections extracted
          for version {extractionStatus.versionId}.
        </div>
      )}
      {showExtractionStatus && extractionStatus && extractionStatus.status === 'failed' && (
        <div className="error-message" data-testid="extraction-failed">
          Extraction failed for version {extractionStatus.versionId}.
          {extractionStatus.error && ` Error: ${extractionStatus.error}`}
        </div>
      )}

      {/* Upload Form */}
      <form onSubmit={handleUpload} data-testid="upload-form">
        <h3>Upload Special Regulations PDF</h3>

        {uploadSuccess && (
          <div className="success-message" data-testid="upload-success">
            {uploadSuccess}
          </div>
        )}
        {uploadError && (
          <div className="error-message" data-testid="upload-error">
            {uploadError}
          </div>
        )}

        <div className="form-field">
          <label htmlFor="special-reg-file">PDF File:</label>
          <input
            id="special-reg-file"
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            data-testid="input-file"
          />
        </div>

        <div className="form-field">
          <label htmlFor="special-reg-effective-date">Effective Date:</label>
          <input
            id="special-reg-effective-date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            data-testid="input-effective-date"
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={uploading || !uploadFile || !effectiveDate}
            data-testid="upload-submit-btn"
          >
            {uploading ? 'Uploading…' : 'Upload & Extract'}
          </button>
        </div>
      </form>

      {/* Extraction Results Summary */}
      {!versionsLoading && versions.length > 0 && (
        <div data-testid="results-section">
          <h3>Extraction Results (Latest Version)</h3>

          {sectionsLoading && <p data-testid="sections-loading">Loading sections…</p>}
          {sectionsError && (
            <div className="error-message" data-testid="sections-error">
              {sectionsError}
            </div>
          )}

          {!sectionsLoading && !sectionsError && sections.length === 0 && (
            <p data-testid="no-sections">No sections found for the current version.</p>
          )}

          {!sectionsLoading && !sectionsError && sectionGroups.length > 0 && (
            <div data-testid="section-groups">
              <p data-testid="total-section-count">
                Total: {sections.length} section{sections.length !== 1 ? 's' : ''} across {sectionGroups.length} group{sectionGroups.length !== 1 ? 's' : ''}
              </p>
              {sectionGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.requirementType);
                return (
                  <div
                    key={group.requirementType}
                    className="category-section"
                    data-testid={`group-${group.requirementType}`}
                  >
                    <div
                      className="category-header"
                      onClick={() => toggleGroup(group.requirementType)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ marginRight: '8px' }}>{isExpanded ? '▼' : '▶'}</span>
                      <h4 style={{ display: 'inline' }}>
                        {REQUIREMENT_TYPE_LABELS[group.requirementType] ?? group.requirementType}
                      </h4>
                      <span className="requirement-count">
                        ({group.sections.length} section{group.sections.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="category-details">
                        <table className="requirements-table" data-testid={`table-${group.requirementType}`}>
                          <thead>
                            <tr>
                              <th>Section</th>
                              <th>Title</th>
                              <th>Categories</th>
                              <th>Page</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.sections.map((section) => (
                              <tr key={section.sectionNumber} data-testid={`section-row-${section.sectionNumber}`}>
                                <td>{section.sectionNumber}</td>
                                <td>{section.title}</td>
                                <td>{section.categories.join(', ')}</td>
                                <td>{section.pageNumber}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpecialRegulationsManager;
